// Libraries
import { cloneDeep } from "lodash-es";
import { Grid, Typography, Stack } from "@mui/material";
// Types
import { IPayrollProviderPayItemOption, ISystemSettingsMappedPayItem } from "@/Settings/types/PayrollMapping/api";
import { TSelectedValuesInput } from "@/Layouts/types/HierarchicalSelector";

// Components
import PayItemRow from "./Row";

type TMappingSectionProps = {
	title: string;
	disabled: boolean;
	payItemsOptions: IPayrollProviderPayItemOption[];
	mappedPayItems: ISystemSettingsMappedPayItem[];
	allMappedPayItems: ISystemSettingsMappedPayItem[];
	setMappedPayItems: React.Dispatch<React.SetStateAction<ISystemSettingsMappedPayItem[]>>;
};

export default function MappingSection(props: Readonly<TMappingSectionProps>) {
	const { title, payItemsOptions, mappedPayItems, allMappedPayItems, disabled, setMappedPayItems } = props;

	function doAnyChange(actionFn: (mappedPayItems: ISystemSettingsMappedPayItem[]) => ISystemSettingsMappedPayItem[]) {
		const newAllMappedPayItems = cloneDeep(allMappedPayItems); // Use ALL items
		const newMappedPayItemsChanged = actionFn(newAllMappedPayItems);
		setMappedPayItems(newMappedPayItemsChanged);
	}

	function handleOnAddNewInput(parentIndex: number) {
		const targetItem = mappedPayItems[parentIndex];
		doAnyChange((payItems: ISystemSettingsMappedPayItem[]) => {
			// Find the correct item in the full array by _id
			const itemToUpdate = payItems.find((item) => item._id === targetItem._id);
			if (itemToUpdate) {
				const newPayItem = { displayId: "", companyId: "", componentId: "", name: "" };
				itemToUpdate.payrollPayItems.push(newPayItem);
			}
			return payItems;
		});
	}

	function handleOnRemoveInput(parentIndex: number, childIndex: number) {
		const targetItem = mappedPayItems[parentIndex];
		doAnyChange((payItems: ISystemSettingsMappedPayItem[]) => {
			const itemToUpdate = payItems.find((item) => item._id === targetItem._id);
			if (itemToUpdate) {
				itemToUpdate.payrollPayItems.splice(childIndex, 1);
			}
			return payItems;
		});
	}

	function handleOnChangeInput(parentIndex: number, childIndex: number, value?: TSelectedValuesInput) {
		const targetItem = mappedPayItems[parentIndex];
		doAnyChange((payItems: ISystemSettingsMappedPayItem[]) => {
			const itemToUpdate = payItems.find((item) => item._id === targetItem._id);
			if (itemToUpdate) {
				const currentValue = itemToUpdate.payrollPayItems[childIndex];
				if (!value) {
					currentValue.displayId = "";
					currentValue.companyId = "";
					currentValue.componentId = "";
					currentValue.name = "";
					return payItems;
				}

				const { value: val, extra } = value;

				if (!currentValue.displayId) {
					currentValue.displayId = val;
					currentValue.companyId = extra;
				} else if (!currentValue.name) {
					currentValue.name = val;
					currentValue.componentId = extra;
				} else if (currentValue.name) {
					currentValue.name = "";
					currentValue.componentId = "";
				}
				itemToUpdate.payrollPayItems[childIndex] = currentValue;
			}
			return payItems;
		});
	}
	return (
		<>
			<Grid container direction="row">
				<Grid item sx={{ marginBottom: 1 }}>
					<Typography className="page-title">{title}</Typography>
				</Grid>
				<Grid container direction={"row"} className="min-h-[40px] pb-6 mt-8">
					<Grid item className="position-mapping-rtc-column" xs={2.5}>
						<Typography className="position-mapping-table-header">Pay Item</Typography>
					</Grid>
					<Grid item className="position-mapping-payroll-column" xs={2.5}>
						<Typography className="position-mapping-table-header">Payroll Pay Item</Typography>
					</Grid>
				</Grid>
			</Grid>
			<Stack>
				{mappedPayItems.map(({ _id, payrollPayItems, systemPayItem, effectOnPay }, index, array) => {
					const selectedDisplayIds = payrollPayItems
						? payrollPayItems.filter((input) => input.displayId && input.name).map((input) => input.displayId)
						: [];

					const filteredOptions = payItemsOptions
						.filter(({ displayId }) => !selectedDisplayIds.includes(displayId))
						.map((option) => ({
							...option,
							payItems: option.payItems.filter((payItem) => payItem.effectOnPay === effectOnPay),
						}));
					const generalErrorInSiblingsRow = array.some((item, itemIndex) => {
						if (itemIndex === index) {
							return false;
						}
						return item.payrollPayItems?.some((input) => !input.displayId || !input.name) ?? false;
					});

					return (
						<PayItemRow
							key={_id}
							payItemIndex={index}
							name={systemPayItem}
							value={payrollPayItems}
							options={filteredOptions}
							disabled={disabled || generalErrorInSiblingsRow}
							onAddNewInput={handleOnAddNewInput}
							onRemoveInput={handleOnRemoveInput}
							onChangeInput={handleOnChangeInput}
						/>
					);
				})}
			</Stack>
		</>
	);
}
